<template>
  <label :class="{ 'flex-row flex gap-3': hasLabelOrHint }">
    <SwitchRoot
      @update:checked="(val) => handleUpdateSwitch(val)"
      :checked="checked"
      :disabled="disabled"
      :class="cn(rootVariants({ size, checked, disabled }), props.class)"
      :name="name"
      :value="value"
      id="switch"
    >
      <SwitchThumb :class="cn(thumbVariants({ size, checked, disabled }))">
      </SwitchThumb>
    </SwitchRoot>
    <span v-if="hasLabelOrHint" class="flex flex-col">
      <InputLabel :label="label" :required="required" :size="size">
        <slot></slot>
      </InputLabel>
      <InputHint :hint="hint" :size="size">
        <slot name="hint"></slot>
      </InputHint>
    </span>
  </label>
</template>
<script setup>
import { SwitchRoot, SwitchThumb } from "radix-vue";
import { sizes, rootVariants, thumbVariants } from ".";
import { cn } from "@/Utils/cn";
import { useSlots, computed, ref, watch } from "vue";
import { InputLabel } from "@/SharedComponents/InputLabel";
import { InputHint } from "@/SharedComponents/InputHint";

const emit = defineEmits(["update:checked"]);
const props = defineProps({
  label: { type: String, required: false, default: "" },
  hint: { type: String, required: false, default: "" },
  size: {
    type: null,
    validator: (val) => sizes.includes(val),
    required: false,
    default: "md",
  },
  asChild: { type: Boolean, required: false },
  class: { type: null, required: false },
  checked: { type: [Boolean, String], required: false, default: false },
  disabled: { type: Boolean, required: false, default: false },
  required: { type: Boolean, required: false, default: false },
  name: { type: String, required: false },
  value: { type: [String, Boolean], required: false, default: true },
});

/*
 * The value of the switch when it's checked.
 * @type {Boolean|String}
 * default: true
 */
const internalPositiveValue = ref(
  props.value !== undefined ? props.value : true
);
const slots = useSlots();
const hasLabelOrHint = computed(() => {
  return props.label || slots.default || props.hint || slots.hint;
});
/*
 * The checked state of the switch.
 * @type {Boolean|String}
 * usage v-model:checked
 */
const checked = ref(props.checked);
watch(
  () => props.checked,
  (value) => {
    checked.value = value;
  }
);

const handleUpdateSwitch = (value) => {
  checked.value = value ? internalPositiveValue.value : false;
  emit("update:checked", value);
};
</script>
